import { useEffect, useState, useCallback } from 'react';
import JSZip from 'jszip';
import type { AdResult } from '@/mocks/adResults';
import type { ExportState } from '../types';

interface ExportSuccessModalProps {
  onClose: () => void;
  onOpenUploadGuide: () => void;
  fileCount: number;
  adResult: AdResult | null;
  editedUrls: Record<number, string>;
  state: ExportState;
}

type DownloadStatus = 'idle' | 'fetching' | 'zipping' | 'done' | 'error';

async function fetchImageAsBlob(url: string): Promise<Blob | null> {
  if (!url) return null;
  // Try CORS fetch first
  try {
    const resp = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (resp.ok) return await resp.blob();
  } catch {
    // fall through
  }
  // Fallback: draw via Image → canvas
  try {
    return await new Promise<Blob | null>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const cvs = document.createElement('canvas');
          cvs.width = img.naturalWidth || 1080;
          cvs.height = img.naturalHeight || 1080;
          const ctx = cvs.getContext('2d');
          if (!ctx) { resolve(null); return; }
          ctx.drawImage(img, 0, 0);
          cvs.toBlob((b) => resolve(b), 'image/png');
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
      setTimeout(() => resolve(null), 10_000);
    });
  } catch {
    return null;
  }
}

function buildAdCopyText(adResult: AdResult): string {
  const lines: string[] = [
    '=== ADFORGE EXPORT — AD COPY ===',
    '',
    '--- HEADLINES ---',
    ...(adResult.headlines ?? []).map((h, i) => `${i + 1}. ${h}`),
    '',
    '--- SUBHEADLINES ---',
    ...(adResult.subheadlines ?? []).map((s, i) => `${i + 1}. ${s}`),
    '',
    '--- PRIMARY TEXT ---',
    ...(adResult.primaryText ?? []).map((p, i) => `${i + 1}. ${p}`),
    '',
    '--- CALLS TO ACTION ---',
    ...(adResult.ctas ?? []).map((c, i) => `${i + 1}. ${c}`),
    '',
    '--- HOOKS ---',
    ...(adResult.hooks ?? []).map((h, i) => `${i + 1}. ${h}`),
    '',
    'Generated by AdForge — adforge.ai',
  ];
  return lines.join('\n');
}

function buildPlatformInstructions(state: ExportState): string {
  const platformLabels: Record<string, string> = {
    facebook_feed: 'Facebook Feed (1200×628 or 1080×1080)',
    instagram_feed: 'Instagram Feed (1080×1080)',
    instagram_story: 'Instagram Story / Reels (1080×1920)',
    meta_ad: 'Meta Ads Manager',
    google_display: 'Google Display Network',
    tiktok: 'TikTok Ads',
    linkedin: 'LinkedIn Campaign Manager',
  };
  const lines: string[] = [
    '=== ADFORGE EXPORT — PLATFORM UPLOAD GUIDE ===',
    '',
    'Selected Platforms:',
    ...state.selectedPlatforms.map((p) => `• ${platformLabels[p] ?? p}`),
    '',
    'STEPS:',
    '1. Open your ad platform\'s Ads Manager.',
    '2. Create a new campaign or ad set.',
    '3. Upload the image file(s) from this ZIP.',
    '4. Copy the ad copy from ad-copy.txt into the relevant fields.',
    '5. Review specs and publish.',
    '',
    'FILE FORMAT: ' + state.advanced.fileType.toUpperCase(),
    'QUALITY:     ' + state.advanced.quality.toUpperCase(),
    '',
    'Generated by AdForge — adforge.ai',
  ];
  return lines.join('\n');
}

export default function ExportSuccessModal({
  onClose,
  onOpenUploadGuide,
  fileCount,
  adResult,
  editedUrls,
  state,
}: ExportSuccessModalProps) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>('idle');
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleCopyAdCopy = () => {
    if (!adResult) return;
    const copy = buildAdCopyText(adResult);
    navigator.clipboard.writeText(copy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const handleDownloadZip = useCallback(async () => {
    if (downloadStatus === 'fetching' || downloadStatus === 'zipping') return;

    setDownloadError(null);
    setDownloadProgress(0);
    setDownloadStatus('fetching');

    try {
      const zip = new JSZip();
      const imgFolder = zip.folder('images')!;

      // Collect all image URLs: use edited URL if available, else fall back to storage/regular URL
      const imageUrls: string[] = [];
      const count = Math.max(
        adResult?.imageUrls?.length ?? 0,
        adResult?.imageStorageUrls?.length ?? 0,
        Object.keys(editedUrls).length,
        1,
      );

      for (let i = 0; i < count; i++) {
        const url =
          editedUrls[i] ??
          adResult?.imageStorageUrls?.[i] ??
          adResult?.imageUrls?.[i] ??
          null;
        if (url) imageUrls.push(url);
      }

      // For each selected size, add a copy of each image labelled accordingly
      const sizeLabels: Record<string, string> = {
        square: '1080x1080',
        landscape: '1200x628',
        portrait: '1080x1350',
        story: '1080x1920',
      };

      const ext = state.advanced.fileType === 'jpg' ? 'jpg' : 'png';
      const totalImages = imageUrls.length * state.selectedSizes.length;
      let fetched = 0;

      for (const size of state.selectedSizes) {
        const sizeLabel = sizeLabels[size] ?? size;
        for (let i = 0; i < imageUrls.length; i++) {
          const url = imageUrls[i];
          const filename = `creative-${i + 1}_${sizeLabel}.${ext}`;
          const blob = await fetchImageAsBlob(url);
          if (blob) {
            imgFolder.file(filename, blob);
          } else {
            // If we can't fetch the blob, at least note the URL
            imgFolder.file(`creative-${i + 1}_${sizeLabel}.txt`, `Image URL: ${url}\n\nNote: Could not download this image automatically.\nPlease download it manually from the URL above.`);
          }
          fetched++;
          setDownloadProgress(Math.round((fetched / totalImages) * 80));
        }
      }

      // Add text files if pack format
      if (adResult && (state.exportFormat === 'pack' || state.includedContent.includes('headline'))) {
        zip.file('ad-copy.txt', buildAdCopyText(adResult));
        zip.file('platform-instructions.txt', buildPlatformInstructions(state));
      }

      setDownloadStatus('zipping');
      setDownloadProgress(90);

      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });

      setDownloadProgress(100);

      // Trigger download
      const objectUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `adforge-export-${Date.now()}.zip`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);

      setDownloadStatus('done');
    } catch (err) {
      console.error('ZIP download failed:', err);
      setDownloadError(err instanceof Error ? err.message : 'Download failed. Please try again.');
      setDownloadStatus('error');
    }
  }, [adResult, editedUrls, state, downloadStatus]);

  const isDownloading = downloadStatus === 'fetching' || downloadStatus === 'zipping';

  const downloadButtonLabel = () => {
    if (downloadStatus === 'fetching') return `Fetching files… ${downloadProgress}%`;
    if (downloadStatus === 'zipping') return 'Packaging ZIP…';
    if (downloadStatus === 'done') return 'Downloaded!';
    if (downloadStatus === 'error') return 'Retry Download';
    return 'Download ZIP';
  };

  const downloadButtonIcon = () => {
    if (isDownloading) return 'ri-loader-4-line animate-spin';
    if (downloadStatus === 'done') return 'ri-check-line';
    if (downloadStatus === 'error') return 'ri-error-warning-line';
    return 'ri-download-2-line';
  };

  const downloadButtonColor = () => {
    if (downloadStatus === 'done') return 'bg-green-600 hover:bg-green-700';
    if (downloadStatus === 'error') return 'bg-red-500 hover:bg-red-600';
    return 'bg-orange-500 hover:bg-orange-600';
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${visible ? 'bg-black/70' : 'bg-transparent'}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bg-stone-900 border border-stone-800 rounded-2xl w-full max-w-md transition-all duration-300 overflow-hidden ${
        visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'
      }`}>
        {/* Success header */}
        <div className="relative bg-gradient-to-br from-orange-500/20 via-amber-500/10 to-stone-900 px-6 pt-8 pb-6 text-center border-b border-stone-800">
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 w-7 h-7 flex items-center justify-center rounded-lg text-stone-500 hover:text-white hover:bg-stone-800 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-orange-500/20 border border-orange-500/30 mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-orange-400 text-3xl" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">Your export package is ready</h2>
          <p className="text-sm text-stone-400">
            <span className="text-orange-400 font-semibold">{fileCount} file{fileCount !== 1 ? 's' : ''}</span> generated and ready for download
          </p>
        </div>

        {/* Actions */}
        <div className="p-6 flex flex-col gap-3">

          {/* Download ZIP — primary action */}
          <button
            onClick={handleDownloadZip}
            disabled={isDownloading}
            className={`flex items-center gap-3 w-full px-4 py-3.5 rounded-xl transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed ${downloadButtonColor()}`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${downloadButtonIcon()} text-white text-base`} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-bold text-white">{downloadButtonLabel()}</div>
              {isDownloading ? (
                <div className="mt-1 h-1 bg-white/20 rounded-full overflow-hidden w-full">
                  <div
                    className="h-full bg-white/70 rounded-full transition-all duration-300"
                    style={{ width: `${downloadProgress}%` }}
                  />
                </div>
              ) : (
                <div className="text-xs text-orange-200">All {fileCount} files in one archive</div>
              )}
            </div>
            {!isDownloading && (
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-white/70 text-sm" />
              </div>
            )}
          </button>

          {/* Error message */}
          {downloadError && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-red-400 text-sm" />
              </div>
              <p className="text-xs text-red-300 leading-relaxed">{downloadError}</p>
            </div>
          )}

          <button
            onClick={handleCopyAdCopy}
            disabled={!adResult}
            className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl bg-stone-800 hover:bg-stone-750 border border-stone-700 hover:border-stone-600 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${copied ? 'ri-check-line text-green-400' : 'ri-clipboard-line text-stone-400'} text-base`} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-semibold text-stone-200">{copied ? 'Copied!' : 'Copy Ad Copy'}</div>
              <div className="text-xs text-stone-500">Headlines, body text, hashtags</div>
            </div>
          </button>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleDownloadZip}
              disabled={isDownloading}
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-stone-800 border border-stone-700 hover:border-stone-600 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-folder-open-line text-stone-400 text-sm" />
              </div>
              <span className="text-sm text-stone-300 font-medium whitespace-nowrap">View Files</span>
            </button>
            <button
              onClick={() => { handleClose(); setTimeout(onOpenUploadGuide, 320); }}
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-stone-800 border border-stone-700 hover:border-orange-500/40 transition-all duration-200 cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-guide-line text-orange-400/70 text-sm" />
              </div>
              <span className="text-sm text-stone-300 font-medium whitespace-nowrap">Upload Guide</span>
            </button>
          </div>
        </div>

        <div className="px-6 pb-4 text-center">
          <p className="text-xs text-stone-600">
            Files are ready for Meta Ads Manager, TikTok Ads, Google Ads, and LinkedIn Campaign Manager.
          </p>
        </div>
      </div>
    </div>
  );
}
